import { NavLink, Link, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useBrand } from "../../contexts/BrandContext";
import { BrandLogoCircle } from "../../components/BrandLogoCircle";
import { NotificationBell } from "../../components/NotificationBell";
import { Building2, ClipboardList, UserCog, LogOut, KeyRound, Palette, Users, Contact, Wallet, SlidersHorizontal, FileSignature, PlusCircle, Store, Menu, X, LayoutDashboard, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";
import { http } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { typesForPath } from "../../lib/sections";

/**
 * Shell centrale per PLATFORM_ADMIN. Menu: Saloni / Audit / Profilo / Identità.
 * Le sezioni sono riservate al PLATFORM_ADMIN (protezione anche lato API).
 * Se `must_change_password=true` blocca ogni route eccetto /platform/change-password.
 */
export default function PlatformLayout() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const nav = useNavigate();
  const loc = useLocation();
  const [badges, setBadges] = useState({});

  const fetchBadges = async () => {
    if (!user?.platform_admin) return;
    try { const { data } = await http.get("/crm/me/badges"); setBadges(data || {}); } catch { /* noop */ }
  };
  useEffect(() => { fetchBadges(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);
  usePolling(fetchBadges, { interval: 10000, enabled: !!user?.platform_admin });
  useEffect(() => {
    const h = () => { fetchBadges(); };
    window.addEventListener("glowbit:badges-refresh", h);
    return () => window.removeEventListener("glowbit:badges-refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  // Apertura sezione: marca lette le novità di quella sezione (non le pendenze d'azione) + aggiorna badge/campanella
  useEffect(() => {
    if (!user?.platform_admin) return;
    const types = typesForPath(loc.pathname);
    if (!types) return;
    http.post(`/crm/me/notifications/read-by-type?types=${encodeURIComponent(types.join(","))}`)
      .then(() => { fetchBadges(); window.dispatchEvent(new Event("glowbit:notif-refresh")); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, user]);
  const hasBadges = (badges.candidature || 0) + (badges.annullamenti || 0) + (badges.ticket || 0) + (badges.extra || 0) + (badges.cambi_modalita || 0) + (badges.firme || 0) > 0;

  if (user === null) return null; // loading
  if (!user) return <Navigate to="/platform/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (!user.platform_admin) return <Navigate to="/platform/login" replace />;
  if (user.must_change_password && !loc.pathname.startsWith("/platform/change-password")) {
    return <Navigate to="/platform/change-password" replace />;
  }

  const doLogout = async () => {
    try { await logout(); } catch { /* noop */ }
    nav("/platform/login", { replace: true });
  };

  const doLogoutAll = async () => {
    try {
      await http.post("/saloncentral/me/logout-all");
      toast.success("Sessioni revocate. Effettua nuovamente il login.");
      await logout().catch(() => {});
      nav("/platform/login", { replace: true });
    } catch (e) {
      toast.error("Errore logout-all");
    }
  };

  const menuItem = "flex items-center gap-3 px-4 py-3 text-sm hover:bg-white/5 border-l-2 transition-colors";
  const linkFn = ({ isActive }) =>
    `${menuItem} ${isActive ? "bg-white/10" : "border-transparent"}`;
  const activeStyle = ({ isActive }) =>
    isActive ? { borderLeftColor: "var(--brand-accent)" } : {};
  const badgeCls = "self-center shrink-0 mr-3 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white hover:bg-rose-500";
  const row = (to, testid, Icon, label, count, filterTo) => (
    <div className="flex items-stretch" key={testid}>
      <NavLink to={to} onClick={() => setMenuOpen(false)} className={(s) => `${linkFn(s)} flex-1`} style={activeStyle} data-testid={testid}>
        <Icon className="w-4 h-4" /> {label}
      </NavLink>
      {count > 0 && (
        <Link to={filterTo || to} onClick={() => setMenuOpen(false)} data-testid={`${testid}-badge`} className={badgeCls}>{count}</Link>
      )}
    </div>
  );

  return (
    <div data-testid="platform-shell" className="min-h-screen flex flex-col md:flex-row"
      style={{ background: "var(--brand-dark)", color: "white" }}>
      <aside className="w-full md:w-64 border-b md:border-b-0 md:border-r border-white/10 flex flex-col md:min-h-screen"
        style={{ background: "rgba(0,0,0,0.2)" }}>
        <div className="px-4 py-4 md:py-5 border-b border-white/10 flex items-center gap-3">
          <BrandLogoCircle className="h-10 w-10 md:h-12 md:w-12 shrink-0" imgTestId="platform-brand-logo" />
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight truncate" data-testid="platform-brand-name">{brand.display_name}</div>
            <div className="text-[10px] text-white/40">Console multi-tenant</div>
          </div>
          <button type="button" onClick={() => setMenuOpen((v) => !v)} data-testid="platform-menu-toggle"
            className="ml-auto md:hidden relative p-2 rounded-md hover:bg-white/10" aria-label="Menu" aria-expanded={menuOpen}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            {!menuOpen && hasBadges && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-500" />}
          </button>
        </div>
        <div className={`${menuOpen ? "flex" : "hidden"} md:flex flex-col md:flex-1 min-h-0`} data-testid="platform-menu-panel">
          <nav className="py-2 md:py-4 md:flex-1 overflow-y-auto max-h-[70vh] md:max-h-none">
            <NavLink to="/platform/salon-central" onClick={() => setMenuOpen(false)} className={linkFn} style={activeStyle} data-testid="pnav-salons">
              <Building2 className="w-4 h-4" /> Saloni
            </NavLink>
            <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-widest text-white/30">CRM commerciale</div>
            {row("/platform/crm/dashboard", "pnav-dashboard", LayoutDashboard, "Dashboard commerciale", 0)}
            {row("/platform/crm/venditori", "pnav-venditori", Users, "Venditori", badges.candidature, "/platform/crm/venditori?status=candidatura_inviata")}
            {row("/platform/crm/contatti", "pnav-contatti", Contact, "Contatti e clienti", 0)}
            {row("/platform/crm/trattative", "pnav-trattative", ClipboardList, "Trattative", 0)}
            {row("/platform/crm/provvigioni", "pnav-provvigioni", Wallet, "Provvigioni", badges.annullamenti, "/platform/crm/provvigioni?filter=annullamenti")}
            {row("/platform/crm/pagamenti", "pnav-pagamenti", Wallet, "Pagamenti", 0)}
            {row("/platform/crm/offerte", "pnav-offerte", ClipboardList, "Offerte", 0)}
            {row("/platform/crm/progetti", "pnav-progetti", Building2, "Progetti", badges.ticket, "/platform/crm/progetti?filter=ticket")}
            {row("/platform/crm/extra", "pnav-extra", PlusCircle, "Extra", badges.extra, "/platform/crm/extra?filter=da-gestire")}
            {row("/platform/crm/vendite-dirette", "pnav-vendite-dirette", Store, "Vendite dirette", 0)}
            {row("/platform/crm/modalita", "pnav-modalita", SlidersHorizontal, "Cambio modalità", badges.cambi_modalita, "/platform/crm/modalita")}
            {row("/platform/crm/report", "pnav-report", ClipboardList, "Report e strumenti", 0)}
            {row("/platform/crm/impostazioni", "pnav-impostazioni", SlidersHorizontal, "Impostazioni CRM", 0)}
            {row("/platform/crm/backup", "pnav-backup", DatabaseBackup, "Backup e ripristino", 0)}
            {row("/platform/crm/materiali", "pnav-materiali", FileSignature, "Materiali di vendita", 0)}
            {row("/platform/crm/documenti", "pnav-documenti", FileSignature, "Documenti firmabili", badges.firme, "/platform/crm/documenti?filter=da-firmare")}
            <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-widest text-white/30">Centrale</div>
            {row("/platform/audit", "pnav-audit", ClipboardList, "Audit centrale", 0)}
            {row("/platform/profile", "pnav-profile", UserCog, "Profilo e sicurezza", 0)}
            {row("/platform/identity", "pnav-identity", Palette, "Identità", 0)}
          </nav>
          <div className="border-t border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Sessione</div>
            <div className="text-xs mb-1 break-all" data-testid="platform-user-email">{user.email}</div>
            <div className="text-[10px] mb-3">
              <span className="px-2 py-0.5 uppercase tracking-widest rounded"
                style={{ background: "var(--brand-primary)", color: "white" }} data-testid="platform-role-badge">
                PLATFORM_ADMIN
              </span>
            </div>
            <button onClick={doLogoutAll} data-testid="platform-logout-all"
              className="w-full text-left text-[11px] py-1 text-white/60 hover:text-white/90 flex items-center gap-2">
              <KeyRound className="w-3 h-3" /> Esci da tutti i dispositivi
            </button>
            <button onClick={doLogout} data-testid="platform-logout"
              className="w-full text-left text-[11px] py-1 text-white/60 hover:text-white/90 flex items-center gap-2">
              <LogOut className="w-3 h-3" /> Esci
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-h-screen overflow-x-hidden" style={{ background: "var(--bg-main)", color: "var(--text-primary)" }}>
        <header className="border-b px-6 py-4 flex items-center gap-3" data-testid="platform-header"
          style={{ borderColor: "var(--border-silver)", background: "var(--bg-card)" }}>
          <img src={brand.logo_url} alt="" className="h-7 w-auto"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="font-serif text-xl">{brand.display_name}</span>
          <div className="ml-auto flex items-center gap-4">
            <NotificationBell tone="light" />
            <span className="text-xs text-neutral-500 hidden sm:inline">Ambiente centrale (non tenant)</span>
          </div>
        </header>
        <div className="p-2 md:p-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
